import React, { useState, useEffect } from 'react';
import { proveedoresAPI } from '../api/client';
import { Truck, Plus, Search, Edit, Trash2, Mail, Phone } from 'lucide-react';
import ProveedorModal from '../components/ProveedorModal';

const ProveedoresPage = () => {
  const [proveedores, setProveedores] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [proveedorEditar, setProveedorEditar] = useState(null);

  useEffect(() => {
    loadProveedores();
  }, []);

  const loadProveedores = async () => {
    try {
      const response = await proveedoresAPI.getAll();
      if (response.data.success) {
        setProveedores(response.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setProveedores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      let response;
      if (proveedorEditar) {
        response = await proveedoresAPI.update(proveedorEditar.id, formData);
      } else {
        response = await proveedoresAPI.create(formData);
      }

      if (response.data.success) {
        alert(proveedorEditar ? 'Proveedor actualizado' : 'Proveedor creado');
        setIsModalOpen(false);
        setProveedorEditar(null);
        loadProveedores();
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      alert('Error al guardar proveedor');
    }
  };

  const handleEdit = (proveedor) => {
    setProveedorEditar(proveedor);
    setIsModalOpen(true);
  };

  const handleNew = () => {
    setProveedorEditar(null);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este proveedor?')) return;
    
    try {
      const response = await proveedoresAPI.delete(id);
      if (response.data.success) {
        alert('Proveedor eliminado');
        loadProveedores();
      }
    } catch (error) {
      alert('Error al eliminar');
    }
  };

  const proveedoresFiltrados = proveedores.filter(proveedor =>
    proveedor.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
    proveedor.email?.toLowerCase().includes(busqueda.toLowerCase()) ||
    proveedor.telefono?.includes(busqueda)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Proveedores</h1>
        <button 
          onClick={handleNew}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nuevo Proveedor
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar proveedores..."
              className="input-field pl-10"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFC</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : proveedoresFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    {busqueda ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
                  </td>
                </tr>
              ) : (
                proveedoresFiltrados.map((proveedor) => (
                  <tr key={proveedor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Truck className="w-5 h-5 text-gray-400 mr-2" />
                        {proveedor.nombre}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {proveedor.email && (
                        <div className="flex items-center text-gray-600">
                          <Mail className="w-4 h-4 mr-1" />
                          {proveedor.email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {proveedor.telefono && (
                        <div className="flex items-center text-gray-600">
                          <Phone className="w-4 h-4 mr-1" />
                          {proveedor.telefono}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">{proveedor.rfc || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleEdit(proveedor)}
                          className="text-blue-600 hover:bg-blue-50 p-2 rounded"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(proveedor.id)}
                          className="text-red-600 hover:bg-red-50 p-2 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ProveedorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        proveedor={proveedorEditar}
      />
    </div>
  );
};

export default ProveedoresPage;
